import { AppFrameWide } from "./AppFrameWide";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppFrameA11y - THE CONTACT PAGE INSIDE THE APP'S FRAME, HELD TO THE SAME RULES AS THE APP (audit 2026-10-01).
//
// The contact page is a RECORD the service writes into the subscriber's Dropbox (`DossierHtml`): script-free, readable on
// its own, and already in Dropbox for every contact. So what the audit found IN THE FRAME is fixed FROM OUTSIDE it, the
// way `AppReactions`, `AppTaskFrame` and `AppDayFold` work - a stylesheet in the app's frame sheet list (`Css`) and a
// few attributes and one key listener set when the frame loads (`Attach`). ⭐ EVERY PAGE ALREADY WRITTEN IS FIXED AT
// ONCE: nothing here needs a rebuild, and no page's bytes change (nothing is re-pushed).
//
// WHAT WAS FOUND, measured in real Chrome at 320-1600px, light and dark:
//   * no visible focus on the task tick, the reaction row's buttons and the "…" menus (a colour change or nothing);
//   * times beside the owner's own words at 4.4:1 (light) and 3.2:1 (dark); day counts at 65% strength (2.5:1);
//     the "3 d late" chip at 2.3:1; text at 10.5 and 11px;
//   * Insights / Details / "+3 more" / "N open" are `<label role="button" tabindex="0">` for a hidden checkbox: Tab
//     reached them, Enter and Space did nothing (a label answers a click, not a key); the checkbox was a SECOND, unseen
//     tab stop for the same thing; Esc closed neither drawer; the Details checkbox had no name when the page has no
//     Details;
//   * on a phone every control in the page was under 44 px: the tick (22), "…" (24), the go-to-message arrow, the day
//     pills, Insights/Details, the reaction opener (28) and its row (38), Undo, Copy, the flag;
//   * the page's own header said the relationship and the standing again, right under the app's header.
//
// ⛔ ATTRIBUTES AND FOCUS ONLY: nothing here clicks a sender or writes anything (decision 9); the one click it makes is
// the page's own label (opening a drawer), for the key the owner pressed on it.

/** The little of an element this touches - the browser's own, or a test's. */
export interface IFa11yEl {
    checked?: boolean;
    tagName?: string;
    textContent?: string | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    removeAttribute?(Name: string): void;
    hasAttribute?(Name: string): boolean;
    closest?(Selector: string): IFa11yEl | null;
    querySelector?(Selector: string): IFa11yEl | null;
    click?(): void;
    focus?(): void;
    addEventListener?(Type: string, Listener: (Ev: IFa11yEvent) => void): void;
}

export interface IFa11yEvent {
    key?: string;
    target?: unknown;
    repeat?: boolean;
    ctrlKey?: boolean;
    metaKey?: boolean;
    altKey?: boolean;
    preventDefault(): void;
}

export interface IFrameA11yDoc {
    getElementById(Id: string): IFa11yEl | null;
    querySelector(Selector: string): IFa11yEl | null;
    querySelectorAll(Selector: string): ArrayLike<IFa11yEl>;
    addEventListener(Type: string, Listener: (Ev: IFa11yEvent) => void, Capture?: boolean): void;
    documentElement?: { clientWidth?: number } | null;
    /** The frame's own window - its `resize` is when "is the Details column showing" can change. */
    defaultView?: { addEventListener(Type: string, Listener: () => void): void } | null;
}

export interface IFa11yFrame {
    contentDocument: IFrameA11yDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

export class AppFrameA11y {
    /** The page's drawers: the checkbox that holds each one open. */
    public static readonly InsightsId: string = "wac-insights";
    public static readonly DetailsId: string = "wac-details";
    /** From this frame width the Details are a COLUMN (shown unless ticked), not a sheet over the chat (`DossierHtml.DetailsCss`). */
    public static readonly DetailsColumnMin: number = 1000;
    /** A label that works a checkbox and says it is a button (`DossierHtml`, `TaskStateHtml`). */
    public static readonly LabelButton: string = "label[role=\"button\"][for]";

    private Frame: IFa11yFrame | null = null;
    private Doc: IFrameA11yDoc | null = null;
    /**
     * Installs the tooltips on the frame's document once it has loaded - handed in by the page (`AppMain.Frame` passes
     * `AppTips.Install`), so this file needs no browser module and a suite can pass a recorder. null = none.
     */
    public Tips: ((Doc: IFrameA11yDoc) => void) | null = null;

    /** Attached BEFORE the frame's `src` is set, so its `load` cannot be missed. */
    public Attach = (Frame: IFa11yFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.Attach");
        }
    };

    private OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null || this.Frame.contentDocument === null || this.Doc === this.Frame.contentDocument) { return; }
            this.Doc = this.Frame.contentDocument;
            AppFrameA11y.Apply(this.Doc as IFrameA11yDoc);
            (this.Doc as IFrameA11yDoc).addEventListener("keydown", this.OnKey, true);
            (this.Doc as IFrameA11yDoc).addEventListener("change", this.OnChange, true);
            const View: { addEventListener(Type: string, Listener: () => void): void } | null | undefined = (this.Doc as IFrameA11yDoc).defaultView;
            if (View !== null && View !== undefined) { View.addEventListener("resize", this.OnResize); }
            // ⭐ The page's tooltips reach a finger and the keyboard too (`AppTips` - one per document).
            if (this.Tips !== null) { this.Tips(this.Doc as IFrameA11yDoc); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.OnLoad");
        }
    };

    /**
     * The attributes a page already written lacks:
     *   * each label-button says whether what it opens is open (`aria-expanded`), and its checkbox stops being a second,
     *     unseen tab stop for the same thing (`tabindex="-1"`, `aria-hidden`);
     *   * a drawer's checkbox with NO button on the page (no Details to show) is taken out of the tab order and hidden
     *     from a screen reader - it was an unnamed, invisible checkbox.
     * ⛔ Attributes only. Run again on a page it has seen, it changes nothing.
     */
    public static Apply = (Doc: IFrameA11yDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Worked: Record<string, boolean> = {};
            // ⭐ A DRAWER'S OWN "Close" IS A LABEL TOO, AND THE KEYBOARD COULD NOT REACH IT (audit 2026-10-12): it becomes
            // a button the Tab key stops at, worked by the same Enter / Space listener. It says no `aria-expanded` -
            // it only ever closes.
            const Closers: ArrayLike<IFa11yEl> = Doc.querySelectorAll(AppFrameA11y.CloseLabel);
            for (let At: number = 0; At < Closers.length; At = At + 1) {
                if (Closers[At].getAttribute("role") !== "button") { Closers[At].setAttribute("role", "button"); }
                if (Closers[At].getAttribute("tabindex") === null) { Closers[At].setAttribute("tabindex", "0"); }
            }
            const Labels: ArrayLike<IFa11yEl> = Doc.querySelectorAll(AppFrameA11y.LabelButton);
            for (let At: number = 0; At < Labels.length; At = At + 1) {
                const Id: string = nz(Labels[At].getAttribute("for"));
                const Box: IFa11yEl | null = Doc.getElementById(Id);
                if (Box === null) { continue; }
                if ((" " + nz(Labels[At].getAttribute("class")) + " ").indexOf(" closebtn ") >= 0) { continue; }
                Worked[Id] = true;
                Labels[At].setAttribute("aria-expanded", String(AppFrameA11y.OpenNow(Doc, Id, Box)));
                AppFrameA11y.Quiet(Box);
            }
            AppFrameA11y.NameTables(Doc);
            AppFrameA11y.Rest(Doc, AppFrameA11y.InsightsId, AppFrameA11y.InsightsPanel);
            AppFrameA11y.Rest(Doc, AppFrameA11y.DetailsId, AppFrameA11y.DetailsPanel);
            for (const Id of [AppFrameA11y.InsightsId, AppFrameA11y.DetailsId]) {
                const Box: IFa11yEl | null = Doc.getElementById(Id);
                if (Box !== null && Worked[Id] !== true) { AppFrameA11y.Quiet(Box); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.Apply");
        }
    };

    /** What each drawer's checkbox opens (`DossierHtml`): the Insights drawer and the Details column / sheet. */
    public static readonly InsightsPanel: string = ".drawer";
    public static readonly DetailsPanel: string = ".details";

    /**
     * ⭐ A SHUT DRAWER IS OUT OF REACH (audit 2026-10-12). The page slides its drawers off the screen with a transform,
     * so everything inside a SHUT one stayed in the tab order: Tab walked through the Insights drawer's "Show the
     * numbers", the kept blocks' buttons and the Details links with nothing on screen showing where the focus was, and
     * a screen reader read both drawers on every page. `inert` on the shut panel takes it out for the keyboard and
     * the reader alike; it is removed the moment the drawer is open. Attributes only; run again, it changes nothing.
     * ⚠ "Open" is `OpenNow`'s answer, so the Details COLUMN of a wide page (shown while its box is NOT ticked) is never
     * put to rest - and `OnResize` asks again when the width crosses that line.
     */
    private static Rest = (Doc: IFrameA11yDoc, Id: string, PanelSel: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Box: IFa11yEl | null = Doc.getElementById(Id);
            const Panel: IFa11yEl | null = Doc.querySelector(PanelSel);
            if (Box === null || Panel === null) { return; }
            const Open: boolean = AppFrameA11y.OpenNow(Doc, Id, Box);
            const Resting: boolean = Panel.getAttribute("inert") !== null;
            if (Open === true && Resting === true && typeof Panel.removeAttribute === "function") { Panel.removeAttribute("inert"); }
            if (Open === false && Resting === false) { Panel.setAttribute("inert", ""); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.Rest");
        }
    };

    /** A drawer's own "Close" (`DossierHtml`): `<label class="closebtn" for="wac-details">`. */
    public static readonly CloseLabel: string = "label.closebtn[for]";
    /** The name given to the Insights chart's "Show the numbers" table, which the page writes without a caption. */
    public static readonly NumbersTable: string = "Messages per period";

    /**
     * ⭐ A TABLE WITH NO NAME GETS ONE (audit 2026-10-12): the activity chart's table of numbers has no `<caption>`, so a
     * screen reader announced "table" and nothing else. Attributes only; a table that already says what it is is left.
     */
    private static NameTables = (Doc: IFrameA11yDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⚠ ONLY the chart's own table (`ActivityChartHtml`: `details.acttab`) - a table kept from an AI answer is named
            // below, by its own title, and must never be called this.
            const Tables: ArrayLike<IFa11yEl> = Doc.querySelectorAll("details.acttab table:not([aria-label]):not([aria-labelledby])");
            for (let At: number = 0; At < Tables.length; At = At + 1) {
                const One: IFa11yEl = Tables[At];
                if (typeof One.querySelector === "function" && One.querySelector("caption") !== null) { continue; }
                One.setAttribute("aria-label", AppFrameA11y.NumbersTable);
            }
            // A table kept from an AI answer ("Kept from AI", `PinHtml`) that has no caption is named by the title its
            // own block shows above it (`.pint`); with no title to read, "Table" - the AI panel's own word for one.
            const Kept: ArrayLike<IFa11yEl> = Doc.querySelectorAll(".pin .pintable table:not([aria-label]):not([aria-labelledby])");
            for (let At: number = 0; At < Kept.length; At = At + 1) {
                const One: IFa11yEl = Kept[At];
                if (typeof One.querySelector === "function" && One.querySelector("caption") !== null) { continue; }
                let Name: string = "";
                let Pin: IFa11yEl | null = null;
                if (typeof One.closest === "function") { Pin = One.closest(".pin"); }
                if (Pin !== null && typeof Pin.querySelector === "function") {
                    const Title: IFa11yEl | null = Pin.querySelector(".pint");
                    if (Title !== null) { Name = nz(Title.textContent).trim(); }
                }
                if (Name.length === 0) { Name = "Table"; }
                One.setAttribute("aria-label", Name);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.NameTables");
        }
    };

    /** A checkbox the keyboard and a screen reader reach through its label-button (or not at all). */
    private static Quiet = (Box: IFa11yEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Box.getAttribute("tabindex") !== "-1") { Box.setAttribute("tabindex", "-1"); }
            if (Box.getAttribute("aria-hidden") !== "true") { Box.setAttribute("aria-hidden", "true"); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.Quiet");
        }
    };

    /**
     * Is what the checkbox `Id` holds open now? Ticked = open - except the Details on a wide frame, where they are a
     * column that a tick HIDES (`DossierHtml.DetailsCss`).
     */
    public static OpenNow = (Doc: IFrameA11yDoc, Id: string, Box: IFa11yEl): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Ticked: boolean = Box.checked === true;
            if (Id === AppFrameA11y.DetailsId && AppFrameA11y.WidthOf(Doc) >= AppFrameA11y.DetailsColumnMin) { return Ticked === false; }
            return Ticked;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.OpenNow");
            return false;
        }
    };

    private static WidthOf = (Doc: IFrameA11yDoc): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (Doc.documentElement === null || Doc.documentElement === undefined) { return 0; }
            const Width: number | undefined = Doc.documentElement.clientWidth;
            if (typeof Width !== "number") { return 0; }
            return Width;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.WidthOf");
            return 0;
        }
    };

    /** The frame changed width: whether the Details column is showing may have changed with it (`Rest`). */
    private OnResize = (): void => { this.Refresh("AppFrameA11y.OnResize"); };

    /** A checkbox changed (a click on its label, or `CloseDrawer`): its label-buttons say the new state. */
    private OnChange = (Ev: IFa11yEvent): void => { this.Refresh("AppFrameA11y.OnChange"); };

    /** The one re-apply both handlers share; `Where` is the handler's own name for an error report. */
    private Refresh = (Where: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            AppFrameA11y.Apply(this.Doc as IFrameA11yDoc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Where);
        }
    };

    /**
     * Enter or Space on a label-button presses it. ⚠ A HELD KEY IS ONE PRESS (`repeat`), and a key with Ctrl / Cmd / Alt
     * is somebody else's. The label's own click then ticks the checkbox, exactly as a pointer's would.
     */
    private OnKey = (Ev: IFa11yEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppFrameA11y.IsPress(Ev) === false) { return; }
            const Node: IFa11yEl | null = Ev.target as IFa11yEl | null;
            if (Node === null || Node === undefined || typeof Node.closest !== "function") { return; }
            const Label: IFa11yEl | null = Node.closest(AppFrameA11y.LabelButton);
            if (Label === null || typeof Label.click !== "function") { return; }
            Ev.preventDefault();
            Label.click();
            // ⚠ The drawer's own Close shuts the drawer UNDER the focus (a shut drawer rests, `Rest`): the focus is
            // handed to the button that opens that drawer, as Esc does (`CloseDrawer`) - never left to fall to the page.
            if ((" " + nz(Label.getAttribute("class")) + " ").indexOf(" closebtn ") >= 0 && this.Doc !== null) {
                const Opener: IFa11yEl | null = (this.Doc as IFrameA11yDoc).querySelector("label.openbtn[for=\"" + nz(Label.getAttribute("for")) + "\"]");
                if (Opener !== null && typeof Opener.focus === "function") { Opener.focus(); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.OnKey");
        }
    };

    /** A plain Enter or Space, pressed once. */
    public static IsPress = (Ev: IFa11yEvent): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Ev.key !== "Enter" && Ev.key !== " ") { return false; }
            return Ev.repeat !== true && Ev.ctrlKey !== true && Ev.metaKey !== true && Ev.altKey !== true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.IsPress");
            return false;
        }
    };

    /**
     * Esc: the page's open drawer is shut - Insights first (it lies over everything), then the Details SHEET (on a wide
     * frame the Details are a column, which Esc leaves alone) - and the focus goes back to the button that opens it.
     * True = one was shut. ⛔ One layer per press.
     */
    public static CloseDrawer = (Doc: IFrameA11yDoc): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            for (const Id of [AppFrameA11y.InsightsId, AppFrameA11y.DetailsId]) {
                const Box: IFa11yEl | null = Doc.getElementById(Id);
                if (Box === null || Box.checked !== true) { continue; }
                if (Id === AppFrameA11y.DetailsId && AppFrameA11y.WidthOf(Doc) >= AppFrameA11y.DetailsColumnMin) { continue; }
                Box.checked = false;
                AppFrameA11y.Apply(Doc);
                const Opener: IFa11yEl | null = Doc.querySelector("label.openbtn[for=\"" + Id + "\"]");
                if (Opener !== null && typeof Opener.focus === "function") { Opener.focus(); }
                return true;
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameA11y.CloseDrawer");
            return false;
        }
    };

    /**
     * The frame sheet (LAST in `AppMain.Frame`'s list, so it has the final word at equal weight). Tokens only: the
     * frame carries every app token (`AppTokens.FrameVars`), in the owner's theme.
     * DOCUMENTED EXCEPTION: a constant. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        const S: string = "html[data-wac-embedded]";
        return AppFrameA11y.FocusCss(S) + AppFrameA11y.ReadCss(S) + AppFrameA11y.TouchCss(S);
    };

    /** One focus ring for every control in the page, and none for what only the page itself focuses. */
    private static FocusCss = (S: string): string => {
        return S + " :focus-visible{outline:2px solid var(--accent);outline-offset:2px}"
            + S + " [tabindex=\"-1\"]:focus{outline:none}"
            + S + " .wac-tftick:focus-visible," + S + " .wac-rxrow button:focus-visible," + S + " .wac-tfpop button:focus-visible"
            + "{outline:2px solid var(--accent);outline-offset:2px}"
            + S + " .detailsbody summary:focus-visible{outline:2px solid var(--accent);outline-offset:-2px}"
            + S + " label[role=\"button\"]{cursor:pointer}";
    };

    /** Contrast and size as rendered, and the header's repeated words. */
    private static ReadCss = (S: string): string => {
        // The app's header (`AppChatHead`) already says who this is and how the chat stands.
        return S + " header.bar .idn .rel," + S + " header.bar .idn .l2{display:none}"
            + S + " .b .t," + S + " .b .wac-state," + S + " .bar .pill," + S + " .openbtn .n," + S + " .detailsbody summary .n{font-size:12px}"
            + S + " .dayhead i{opacity:1}"
            // Beside the owner's own words the time sits on the bubble's green: its own ink, 4.5:1 in both themes.
            + S + " .b.me .t," + S + " .b.me .wac-state{color:var(--onout)}"
            // A link in the owner's own words: the bubble's ink, underlined (the accent was 3.2:1 on the dark green).
            + S + " .b.me a{color:inherit;text-decoration:underline}"
            + S + " .tk .due{background:var(--page);color:var(--text)}"
            + S + " .tk .due.u-overdue{background:var(--danger-soft);color:var(--danger)}"
            + S + " .tk .due.u-today{background:var(--accent-soft);color:var(--accent)}"
            // ⭐ Audit 2026-10-12, measured in the frame: a ticked task's chips were at half strength (2.4:1) - muted ink
            // on the page colour says "done" at 4.5:1; the Details chips ("secret", "inferred") were 10px capitals at
            // 3.9:1; a reaction's count and the translated bubble's Copy were 11.5 and 11.2px.
            + S + " .tk[data-state=done] .due," + S + " .tk[data-state=dismissed] .due{opacity:1;background:var(--page);color:var(--muted)}"
            + S + " .tk[data-state=done] .who{opacity:1;color:var(--muted)}"
            + S + " .st{font-size:12px;text-transform:none;letter-spacing:0}"
            + S + " .st.s-secret{background:var(--warning-soft);color:var(--warning-ink)}"
            + S + " .b .rx i," + S + " .b .trc{font-size:12px}"
            // A long task label wraps on a narrow page instead of being cut with nothing giving the whole of it.
            // ⚠ WORDS, NOT LETTERS (verifier, 2026-10-12): with `overflow-wrap:anywhere` the label could shrink to one letter
            // a line beside its chips ("Confi / rm / the / plum / ber" at 320px). It breaks between words now; where the
            // row is too narrow for label AND chips (a 320px screen) the label takes the first line and the chips the next.
            + "@media(max-width:620px){" + S + " .tk .tl{white-space:normal;overflow:visible;overflow-wrap:break-word}}"
            + "@media(max-width:340px){" + S + " .tk{flex-wrap:wrap;row-gap:2px}" + S + " .tk .tl{flex:1 1 60%}}"
            // ⚠ A SHORT FRAME KEEPS ROOM FOR THE MESSAGES: the task strip (three rows and more) filled the whole of it on
            // a 320x640 screen - not one message showed. Under 520px of height it takes two fifths at most and scrolls.
            + "@media(max-height:520px){" + S + " .tasks{max-height:40vh;overflow-y:auto}}"
            + S + " .wac-tftick{border-color:var(--line-strong)}"
            + S + " .wac-tftick[aria-pressed=true]{border-color:var(--success)}"
            + S + " .wac-tfundo{border-color:var(--line-strong)}";
    };

    /**
     * A phone: every control's hit area is 44 x 44 px or more. Words-buttons grow; a small mark keeps its size and gets
     * an unseen 44px area centred on it.
     */
    private static TouchCss = (Root: string): string => {
        // ⛔ NOT ON THE WIDE SCREEN (`AppFrameWide`): in three panes the frame is narrower than 620px on a desktop with a mouse.
        const S: string = Root + AppFrameWide.Not;
        const Area: string = "{content:\"\";position:absolute;top:min(0px,calc(50% - 22px));bottom:min(0px,calc(50% - 22px));"
            + "left:min(0px,calc(50% - 22px));right:min(0px,calc(50% - 22px))}";
        // In the flow: made `position:relative` so the unseen area is laid out against the mark itself.
        const Flow: string[] = [".wac-tftick", ".wac-tfctl summary", ".tk .tgo", ".tcount", ".tmore", ".dayhead span", ".wac-tfundo",
            ".b .trc", ".closebtn", ".wac-pfrm"];
        // ⚠ Already absolutely placed (the flag beside the clock, the bubble's "Mark done", the reaction opener): they
        // keep their placement - an absolute box is its own containing block for the area.
        const Placed: string[] = [".b .trf", ".b .wac-tfmark", ".wac-rxbtn"];
        let Rel: string = "";
        let After: string = "";
        for (const One of Flow) {
            if (Rel.length > 0) { Rel = Rel + ","; }
            Rel = Rel + S + " " + One;
        }
        for (const One of Flow.concat(Placed)) {
            if (After.length > 0) { After = After + ","; }
            After = After + S + " " + One + "::after";
        }
        return "@media(max-width:620px){"
            + S + " .bar .openbtn," + S + " .wac-tfpop button," + S + " .wac-livepill," + S + " .trearlier," + S + " .trretry," + S + " .trplain,"
            + S + " .detailsbody summary," + S + " .acttab summary{min-height:44px}"
            + S + " .acttab summary{display:flex;align-items:center}"
            + S + " .dtitle{display:inline-flex;align-items:center;min-height:44px;min-width:44px}"
            + S + " .bar .openbtn{display:inline-flex;align-items:center}"
            + S + " .wac-rxrow button{width:44px;height:44px}"
            // ⛔ THE DAY LABEL'S UNSEEN 44px AREA STAYS IN THE LABEL'S OWN ROW (2026-10-13, measured at 320x640: pill
            // 225-248, its area down to 258.7, the day's first bubble from 254). The area reached 5px INTO that bubble: a
            // pointer travelling along the bubble's top edge to "Mark done" left the bubble, the button was put away and
            // the press landed on nothing (the audit's raw mouse); a finger on the message's first line folded the day.
            // 12px above and below the label keeps the whole area between the messages.
            + S + " .dayhead{margin-top:12px;margin-bottom:12px}"
            + Rel + "{position:relative}"
            + After + Area
            + "}";
    };
}
